/**
 * @fileoverview Main Logger class for browser logging
 * @module Logger
 */

import { LOG_LEVEL, LOG_LEVEL_PRIORITY, SENSITIVE_FIELDS } from './constants.js';
import { sanitizeData } from './utils/sanitize.js';
import { redactSensitiveText } from '@puffin-core/redaction';
import { getLogLevelFromEnv } from './utils/environment.js';
import { ConsoleTransport } from './transports/console.js';
import { getTraceContext } from '@puffin-core/tracing-browser';

/** The fields every entry writes itself; a context may not carry one (Q-MB-25). */
export const RESERVED_CONTEXT_KEYS = Object.freeze([
  'timestamp',
  'level',
  'message',
  'source',
  'traceId',
  'spanId',
  'correlationId',
  'sampled',
  'data',
]);

/** A `setContext` key that an entry writes itself. */
export class ReservedContextKeyError extends Error {
  /** @param {string} key - The refused key. */
  constructor(key) {
    super(
      `Logger.setContext: "${key}" is a field every log entry writes itself; a context may not set it`,
    );
    this.name = 'ReservedContextKeyError';
    this.code = 'LOG_CONTEXT_KEY_RESERVED';
    this.key = key;
  }
}

/**
 * Singleton logger instance
 */
let loggerInstance = null;

/**
 * Default configuration
 */
const DEFAULT_CONFIG = {
  level: null, // Will be set from environment
  consoleEnabled: true,
  sanitizeData: true,
  transports: [],
};

/**
 * Logger class for browser logging
 */
export class Logger {
  /**
   * Create logger instance
   * @param {Object} [config] - Logger configuration
   * @param {string} [config.level] - Minimum log level
   * @param {boolean} [config.consoleEnabled=true] - Enable console output
   * @param {boolean} [config.sanitizeData=true] - Enable data sanitization
   * @param {Array} [config.transports] - Additional transports
   */
  constructor(config = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      level: config.level || getLogLevelFromEnv(),
      ...config,
    };
    this.context = {};
    this.transports = [...(this.config.transports || [])];
    // Built-in console transport used when `consoleEnabled` is true. Isolating
    // the console-dispatch logic in ConsoleTransport keeps `no-console` scoped
    // to a single legitimate file instead of being sprinkled through Logger.js.
    this.consoleTransport = new ConsoleTransport({ styled: false });
    // Where `timing()` entries go; installed by `createAppLogger` (see `setMeasurementSink`).
    this.measurementSink = null;
  }

  /**
   * Update logger configuration
   * @param {Object} config - Configuration updates
   */
  configure(config) {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Set context values to include in all logs. The values are sanitized here, like an entry's data,
   * and a key an entry writes itself (`message`, `level`, `source`, the trace ids, …) is refused:
   * a context `source: 'web-vital'` turned every line into a measurement, and a context `message`
   * replaced the redacted one (MUTATION-BURNDOWN-001 Q-MB-25).
   * @param {Object} context - Context key-value pairs
   * @throws {ReservedContextKeyError} When a key is one an entry writes itself.
   */
  setContext(context) {
    const reserved = Object.keys(context || {}).find((key) => RESERVED_CONTEXT_KEYS.includes(key));
    if (reserved !== undefined) {
      throw new ReservedContextKeyError(reserved);
    }
    this.context = {
      ...this.context,
      ...(this.config.sanitizeData ? sanitizeData(context) : context),
    };
  }

  /**
   * Check if a log level should be logged
   * @param {string} level - Log level to check
   * @returns {boolean} True if should log
   */
  shouldLog(level) {
    const configuredPriority = LOG_LEVEL_PRIORITY[this.config.level];
    const messagePriority = LOG_LEVEL_PRIORITY[level];

    // NONE level disables all logging
    if (configuredPriority < 0) {
      return false;
    }

    // Lower priority number = higher importance = always log
    return messagePriority <= configuredPriority;
  }

  /**
   * Create and dispatch a log entry
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @param {string} [options.source='client'] - Log source
   * @returns {Object|null} Log entry or null if filtered
   */
  log(level, message, data = {}, options = {}) {
    if (!this.shouldLog(level)) {
      return null;
    }

    const entry = this.createEntry(level, message, data, options);

    // Output to console if enabled
    if (this.config.consoleEnabled) {
      this.logToConsole(entry);
    }

    // Send to transports
    for (const transport of this.transports) {
      transport.log(entry);
    }

    return entry;
  }

  /**
   * Build a log entry exactly as {@link Logger#log} would — sanitised data, the context bag, the
   * page's trace ids and sampled flag — WITHOUT the threshold, the console or the transports. For
   * a producer that decides its own shipping and hands the entry to a transport's `enqueue`
   * (the web-vitals collector: a measurement, not a log line, so the logger's verbosity threshold
   * — WARNING in production — is not its gate).
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @param {string} [options.source='client'] - Log source
   * @returns {Object} the entry
   */
  createEntry(level, message, data = {}, options = {}) {
    const { source = 'client' } = options;

    // Sanitize data if enabled — and the MESSAGE, which is free text and the likeliest place a value
    // is interpolated (`load failed for ${email}`, an uncaught error's text): Q-SG-23.
    const sanitizedData = this.config.sanitizeData ? sanitizeData(data) : data;
    const sanitizedMessage = this.config.sanitizeData
      ? redactSensitiveText(message, { fields: SENSITIVE_FIELDS })
      : message;

    // Build log entry
    // BROWSER-TELEMETRY-001 P3: every entry carries the page's active trace ids (`traceId` /
    // `spanId` — the PAGE trace, the browser's own span of work).
    // PLN-BOARD-GATE-001 I-12 (owner ruling Q-F-1): a request's trace is its OWN since the
    // browser mints one per request, so the join key between a shipped browser line and the
    // API's request logs is the page id under the SAME name on both sides — `correlationId`
    // (the API records the `x-correlation-id` header the page sends on every request).
    // CLIENT-LOG-SAMPLING-001: the trace's sampled flag rides along so the server transport
    // makes ONE shipping decision per trace (INFO ships iff the trace is sampled).
    const trace = getTraceContext();
    // The context goes FIRST, so no context key can replace a field the entry writes (Q-MB-25;
    // `setContext` refuses one too — belt and braces).
    return {
      ...this.context,
      timestamp: new Date().toISOString(),
      level,
      message: sanitizedMessage,
      source,
      traceId: trace.traceId,
      spanId: trace.spanId,
      correlationId: trace.traceId,
      sampled: trace.sampled === true,
      data: sanitizedData,
    };
  }

  /**
   * Log to console with appropriate method.
   * Uses the built-in ConsoleTransport to resolve the console method so all raw
   * `console.*` dispatch lives in a single transport file (per Gen 0 no-console
   * scoping). Output format is preserved: `[LEVEL] message, data`.
   * @param {Object} entry - Log entry
   */
  logToConsole(entry) {
    const { level, message, data } = entry;
    const consoleMethod = this.consoleTransport.getConsoleMethod(level);
    consoleMethod(`[${level}] ${message}`, data);
  }

  /**
   * Get console method for log level.
   * Delegates to the built-in ConsoleTransport.
   * @param {string} level - Log level
   * @returns {Function} Console method
   */
  getConsoleMethod(level) {
    return this.consoleTransport.getConsoleMethod(level);
  }

  /**
   * Log at TRACE level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  trace(message, data, options) {
    return this.log(LOG_LEVEL.TRACE, message, data, options);
  }

  /**
   * Log at DEBUG level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  debug(message, data, options) {
    return this.log(LOG_LEVEL.DEBUG, message, data, options);
  }

  /**
   * Log at INFO level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  info(message, data, options) {
    return this.log(LOG_LEVEL.INFO, message, data, options);
  }

  /**
   * Log at WARN level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  warn(message, data, options) {
    return this.log(LOG_LEVEL.WARN, message, data, options);
  }

  /**
   * Log at ERROR level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  error(message, data, options) {
    return this.log(LOG_LEVEL.ERROR, message, data, options);
  }

  /**
   * Log at CRITICAL level
   * @param {string} message - Log message
   * @param {Object} [data] - Additional data
   * @param {Object} [options] - Log options
   * @returns {Object|null} Log entry or null if filtered
   */
  critical(message, data, options) {
    return this.log(LOG_LEVEL.CRITICAL, message, data, options);
  }

  /**
   * Log an error object with stack trace
   * @param {Error} error - Error object
   * @param {string} [context=''] - Error context
   * @param {Object} [additionalData] - Additional data
   * @returns {Object|null} Log entry or null if filtered
   */
  logError(error, context = '', additionalData = {}) {
    const data = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...additionalData,
    };

    // The headline is composed IN the log call: a thrown message belongs in a log, and this
    // logger's own `this.log` is the log (`web/no-thrown-message-as-copy`'s logger-class arm, N138).
    return this.log(
      LOG_LEVEL.ERROR,
      context ? `${context}: ${error.message}` : error.message,
      data,
    );
  }

  /**
   * Route MEASUREMENTS (`timing`) to the page's shipper. `createAppLogger` installs the one sink —
   * the same sampled-trace + consent decision the web-vitals collector applies — so a measurement is
   * never gated by the logger's verbosity threshold (WARNING in production would drop every one).
   * @param {((entry: Object) => void)|null} sink - receives each built timing entry; null clears it
   */
  setMeasurementSink(sink) {
    this.measurementSink = typeof sink === 'function' ? sink : null;
  }

  /**
   * Record a client-side TIMING — the ONE measurement API (F-TOCONTAIN-3 U2c; the retired
   * `metric()` logged at DEBUG and so never left the page). Built as an INFO entry
   * `Timing <name>`, `data: { timing, durationMs, ...attributes }` (the measured name and value
   * win over a same-named attribute), `source: 'timing'`, printed to the console in development,
   * and handed to the measurement sink.
   * @param {string} name - e.g. `sales.list.sort_refetch`
   * @param {number} durationMs - finite, non-negative; rounded to whole milliseconds
   * @param {Object} [attributes] - extra dimensions (never the name or value)
   * @returns {Object} the entry
   * @throws {TypeError} on a missing name or a non-finite / negative duration
   */
  timing(name, durationMs, attributes = {}) {
    if (!name || typeof name !== 'string') {
      throw new TypeError('logger.timing requires a name');
    }
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs < 0) {
      throw new TypeError('logger.timing requires a finite, non-negative durationMs');
    }
    const entry = this.createEntry(
      LOG_LEVEL.INFO,
      `Timing ${name}`,
      { ...attributes, timing: name, durationMs: Math.round(durationMs) },
      { source: 'timing' },
    );
    if (this.config.consoleEnabled) {
      this.logToConsole(entry);
    }
    if (this.measurementSink) {
      this.measurementSink(entry);
    }
    return entry;
  }

  /**
   * Add a transport
   * @param {Object} transport - Transport instance
   */
  addTransport(transport) {
    this.transports.push(transport);
  }

  /**
   * Remove a transport
   * @param {Object} transport - Transport instance
   */
  removeTransport(transport) {
    const index = this.transports.indexOf(transport);
    if (index > -1) {
      this.transports.splice(index, 1);
    }
  }
}

/**
 * Create or get singleton logger instance
 * @param {Object} [config] - Logger configuration
 * @returns {Logger} Logger instance
 */
export function createLogger(config) {
  if (!loggerInstance) {
    loggerInstance = new Logger(config);
  }
  return loggerInstance;
}

/**
 * Reset singleton logger instance (for testing)
 */
export function resetLoggerInstance() {
  loggerInstance = null;
}
